// use-replicate-team.ts — Teams suite, action
//
// POST /api/teams/:teamId/replicate. Clones a team into a new team owned by the
// current user.

'use client';

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TeamsListFilter } from '@cabal/shared/domain/session/team/enums.js';
import type { TeamDetail, TeamPreviewListItem } from '@cabal/shared/domain/session/team/types.js';
import { useApi } from '@/contexts/api';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';
import {
  teamsDetailKey,
  teamsPreviewKey,
  teamsRootKey,
} from './team-query-keys';

/**
 * No request payload: a replicate always mints a standalone saved template, so
 * `isSaved: true` is a server-owned consequence of the operation rather than
 * anything a caller supplies.
 */
export interface ReplicateTeamVars {
  teamId: string;
}

function isModelId(value: number | null): value is number {
  return value !== null;
}

function buildPreviewFromTeamDetail(team: TeamDetail): TeamPreviewListItem {
  return {
    id: team.id,
    name: team.name,
    catchPhrase: team.catchPhrase,
    category: team.category,
    tagIds: team.tagIds,
    isPublic: team.isPublic,
    isSaved: true,
    originatedFromTeam: team.originatedFromTeam,
    publicRootTeamId: team.publicRootTeamId,
    ownerId: team.ownerUserId,
    displayOrder: team.displayOrder,
    modelIds: team.members.map((member) => member.modelId).filter(isModelId),
  };
}

function upsertPreviewTeam(input: {
  teams: TeamPreviewListItem[] | undefined;
  team: TeamPreviewListItem;
}): TeamPreviewListItem[] {
  const teams = input.teams ?? [];
  const existingIndex = teams.findIndex((team) => team.id === input.team.id);

  if (existingIndex === -1) {
    return [...teams, input.team];
  }

  return teams.map((team) => (
    team.id === input.team.id ? input.team : team
  ));
}

export function useReplicateTeam(): ActionResult<ReplicateTeamVars, TeamDetail> {
  const api = useApi();
  const queryClient = useQueryClient();

  return useActionHook<ReplicateTeamVars, TeamDetail>(
    useMemo(
      () => ({
        mutationFn: async ({ teamId }) => {
          const replicatedTeam = await api.teams.byId(teamId).replicate();
          return api.teams.byId(replicatedTeam.id).get();
        },
        onInvalidate: (team) => {
          const previewTeam = buildPreviewFromTeamDetail(team);
          queryClient.setQueryData<TeamDetail>(teamsDetailKey(team.id), team);
          // The preview cache is split by filter, so the write goes to the slice
          // the new team actually belongs to. Every team minted here is the
          // viewer's own, hence `owned`; the public slice is touched only in the
          // one case where the team is also in the catalogue.
          queryClient.setQueryData<TeamPreviewListItem[]>(
            teamsPreviewKey(TeamsListFilter.Owned),
            (teams) => upsertPreviewTeam({ teams, team: previewTeam }),
          );
          if (previewTeam.isPublic) {
            queryClient.setQueryData<TeamPreviewListItem[]>(
              teamsPreviewKey(TeamsListFilter.Public),
              (teams) => upsertPreviewTeam({ teams, team: previewTeam }),
            );
          }
          queryClient.invalidateQueries({ queryKey: teamsRootKey });
        },
      }),
      [api, queryClient],
    ),
  );
}
